/**
 * API Client for Fluxiplanner
 */

const API_BASE = normalizeApiBase((import.meta as any).env.VITE_API_URL);

function normalizeApiBase(value?: string): string {
  const raw = String(value || '').trim();
  if (!raw) return '/api';

  const trimmed = raw.replace(/\/+$/, '');
  if (!/^https?:\/\//i.test(trimmed)) {
    return trimmed.endsWith('/api') ? trimmed : `${trimmed}/api`;
  }

  const url = new URL(trimmed);
  if (!url.pathname || url.pathname === '/') {
    url.pathname = '/api';
  }
  return url.toString().replace(/\/+$/, '');
}

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
  };
}

async function request<T>(endpoint: string, options: RequestInit = {}): Promise<ApiResponse<T>> {
  const url = `${API_BASE}${endpoint}`;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };

  const token = localStorage.getItem('flexiplanner-session-token');
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  try {
    const response = await fetch(url, {
      ...options,
      headers,
      credentials: 'include',
    });

    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: data.error || { code: 'UNKNOWN_ERROR', message: 'An error occurred' },
      };
    }

    return data;
  } catch (error) {
    return {
      success: false,
      error: {
        code: 'NETWORK_ERROR',
        message: error instanceof Error ? error.message : 'Network error',
      },
    };
  }
}

// Activities API (backed by the legacy /tasks route)
export const tasksApi = {
  list: () => request<any[]>('/tasks'),
  get: (id: string) => request<any>(`/tasks/${id}`),
  create: (task: any) => request<any>('/tasks', { method: 'POST', body: JSON.stringify(task) }),
  inferPlanning: (payload: { task_ids: string[]; apply?: boolean; limit?: number }) =>
    request<any>('/tasks/infer', { method: 'POST', body: JSON.stringify(payload) }),
  update: (id: string, updates: any) =>
    request<any>(`/tasks/${id}`, { method: 'PATCH', body: JSON.stringify(updates) }),
  delete: (id: string) => request<void>(`/tasks/${id}`, { method: 'DELETE' }),
  reorder: (taskId: string, position: number, parentId?: string) =>
    request<void>(`/tasks/${taskId}/reorder`, {
      method: 'POST',
      body: JSON.stringify({ position, parent_id: parentId }),
    }),
};

// Schedule API
export const scheduleApi = {
  get: (start: string, end: string) => request<any>(`/schedule?start=${start}&end=${end}`),
  getGhostBlocks: (weekStart?: string) =>
    request<any>(`/schedule/ghost-blocks${weekStart ? `?week_start=${weekStart}` : ''}`),
  checkFeasibility: (weekStart?: string) =>
    request<any>(`/schedule/feasibility${weekStart ? `?week_start=${weekStart}` : ''}`),
  scheduleTask: (taskId: string, targetTime?: string) =>
    request<any>(`/schedule/task/${taskId}`, {
      method: 'POST',
      body: JSON.stringify({ target_time: targetTime }),
    }),
  scheduleTaskTomorrow: (taskId: string) =>
    request<any>(`/schedule/task/${taskId}/next-day`, { method: 'POST' }),
  confirmGhostBlock: (ghostBlockId: string) =>
    request<any>(`/schedule/ghost-block/${ghostBlockId}/confirm`, { method: 'POST' }),
  autoSchedule: (weekStart?: string) =>
    request<any>('/schedule/auto', {
      method: 'POST',
      body: JSON.stringify({ week_start: weekStart }),
    }),
  autoScheduleStatus: () => request<any>('/schedule/auto/status'),
  cancelAutoSchedule: () =>
    request<any>('/schedule/auto/cancel', {
      method: 'POST',
    }),
  rollover: () => request<any>('/schedule/rollover', { method: 'POST' }),
  getConstraintOverride: (weekStart?: string) =>
    request<any>(`/schedule/constraint-override${weekStart ? `?week_start=${weekStart}` : ''}`),
  setConstraintOverride: (weekStart: string, enabled: boolean) =>
    request<any>('/schedule/constraint-override', {
      method: 'POST',
      body: JSON.stringify({ week_start: weekStart, enabled }),
    }),
};

// NLP API
export const nlpApi = {
  command: (command: string) =>
    request<any>('/nlp/command', {
      method: 'POST',
      body: JSON.stringify({
        command,
        timezoneOffset: new Date().getTimezoneOffset(),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      }),
    }),
  suggestions: (q: string) => request<any>(`/nlp/suggestions?q=${encodeURIComponent(q)}`),
};

// Calendar API (Google Calendar)
export const calendarApi = {
  getAuthUrl: () => request<{ url: string }>('/calendar/auth/url'),
  getStatus: () => request<any>('/calendar/status'),
  listCalendars: () => request<any[]>('/calendar/calendars'),
  updateCalendars: (calendarIds: string[]) =>
    request<any>('/calendar/calendars', {
      method: 'PUT',
      body: JSON.stringify({ calendarIds }),
    }),
  sync: () => request<any>('/calendar/sync', { method: 'POST' }),
  getEvents: (start?: string, end?: string) => {
    const params = new URLSearchParams();
    if (start) params.set('start', start);
    if (end) params.set('end', end);
    return request<any[]>(`/calendar/events?${params}`);
  },
  updateEvent: (id: string, updates: any) =>
    request<any>(`/calendar/events/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    }),
  revoke: () => request<void>('/calendar/auth/revoke', { method: 'POST' }),
};

// OCR API
export const ocrApi = {
  scan: (file: File) => {
    const formData = new FormData();
    formData.append('image', file);

    const token = localStorage.getItem('flexiplanner-session-token');
    const headers: Record<string, string> = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return fetch(`${API_BASE}/ocr/scan`, {
      method: 'POST',
      body: formData,
      headers,
      credentials: 'include',
    }).then(r => r.json());
  },
  createTasks: (scanId: string, scheduleDate?: string) =>
    request<any>('/ocr/create-tasks', {
      method: 'POST',
      body: JSON.stringify({ scanId, scheduleDate }),
    }),
  getHistory: () => request<any[]>('/ocr/history'),
};

// Training API
export const trainingApi = {
  submitFeedback: (feedback: any) =>
    request<any>('/training/feedback', {
      method: 'POST',
      body: JSON.stringify(feedback),
    }),
  startImmersion: (taskId: string, estimatedDuration: number, startNow = false) =>
    request<any>('/training/immersion/start', {
      method: 'POST',
      body: JSON.stringify({ taskId, estimatedDuration, startNow }),
    }),
  pauseImmersion: (sessionId: string, duration: number, reason?: string) =>
    request<any>(`/training/immersion/${sessionId}/pause`, {
      method: 'POST',
      body: JSON.stringify({ duration, reason }),
    }),
  cancelImmersion: (sessionId: string) =>
    request<any>(`/training/immersion/${sessionId}/cancel`, {
      method: 'POST',
    }),
  endImmersion: (sessionId: string, completed: boolean, energyLevel?: number, details?: any) =>
    request<any>(`/training/immersion/${sessionId}/end`, {
      method: 'POST',
      body: JSON.stringify({ completed, energyLevel, ...details }),
    }),
  logActivitySession: (payload: any) =>
    request<any>('/training/activity-session', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  getActiveImmersion: () => request<any>('/training/immersion/active'),
  getActiveImmersions: () => request<any[]>('/training/immersion/active/all'),
  getAnalytics: () => request<any>('/training/analytics'),
  getEnergyPatterns: () => request<any>('/training/energy-patterns'),
  exportTrainingData: (payload: { format?: 'json' | 'csv'; includeRaw?: boolean } = {}) =>
    request<any>('/training/export', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
};

// State trajectory API
export const stateApi = {
  getTrajectory: (start?: string, end?: string) => {
    const params = new URLSearchParams();
    if (start) params.set('start', start);
    if (end) params.set('end', end);
    return request<any>(`/state/trajectory${params.toString() ? `?${params}` : ''}`);
  },
  listWater: (start?: string, end?: string) => {
    const params = new URLSearchParams();
    if (start) params.set('start', start);
    if (end) params.set('end', end);
    return request<any[]>(`/state/water${params.toString() ? `?${params}` : ''}`);
  },
  logWater: (payload: any) =>
    request<any>('/state/water', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  listSleep: (limit = 30) => request<any[]>(`/state/sleep?limit=${limit}`),
  logSleep: (payload: any) =>
    request<any>('/state/sleep', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  listCheckIns: (limit = 50) => request<any[]>(`/state/check-ins?limit=${limit}`),
  logCheckIn: (payload: any) =>
    request<any>('/state/check-ins', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
};

export const workspaceApi = {
  get: () => request<any>('/workspace'),
  createProject: (project: any) =>
    request<any>('/workspace/projects', {
      method: 'POST',
      body: JSON.stringify(project),
    }),
  updateProject: (id: string, project: any) =>
    request<any>(`/workspace/projects/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(project),
    }),
  deleteProject: (id: string) =>
    request<any>(`/workspace/projects/${id}`, { method: 'DELETE' }),
  createBundle: (bundle: any) =>
    request<any>('/workspace/bundles', {
      method: 'POST',
      body: JSON.stringify(bundle),
    }),
  updateBundle: (id: string, bundle: any) =>
    request<any>(`/workspace/bundles/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(bundle),
    }),
  deleteBundle: (id: string) =>
    request<any>(`/workspace/bundles/${id}`, { method: 'DELETE' }),
};

export const teamsApi = {
  list: () => request<any[]>('/teams'),
  discover: () => request<any[]>('/teams/discover'),
  create: (team: any) =>
    request<any>('/teams', {
      method: 'POST',
      body: JSON.stringify(team),
    }),
  get: (teamId: string) => request<any>(`/teams/${teamId}`),
  update: (teamId: string, updates: any) =>
    request<any>(`/teams/${teamId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    }),
  inviteMember: (teamId: string, member: any) =>
    request<any>(`/teams/${teamId}/members`, {
      method: 'POST',
      body: JSON.stringify(member),
    }),
  requestMemberInvite: (teamId: string, member: any) =>
    request<any>(`/teams/${teamId}/member-invite-requests`, {
      method: 'POST',
      body: JSON.stringify(member),
    }),
  decideMemberInviteRequest: (teamId: string, requestId: string, updates: any) =>
    request<any>(`/teams/${teamId}/member-invite-requests/${requestId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    }),
  acceptInvite: (teamId: string, payload: any) =>
    request<any>(`/teams/${teamId}/invitations/accept`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  updateMember: (teamId: string, memberId: string, updates: any) =>
    request<any>(`/teams/${teamId}/members/${memberId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    }),
  provisionMemberMailAccount: (teamId: string, memberId: string, payload: any) =>
    request<any>(`/teams/${teamId}/members/${memberId}/mail-account`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  requestJoin: (teamId: string, payload: any) =>
    request<any>(`/teams/${teamId}/join-requests`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  updateJoinRequest: (teamId: string, requestId: string, updates: any) =>
    request<any>(`/teams/${teamId}/join-requests/${requestId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    }),
  createJoinRule: (teamId: string, rule: any) =>
    request<any>(`/teams/${teamId}/join-rules`, {
      method: 'POST',
      body: JSON.stringify(rule),
    }),
  deleteJoinRule: (teamId: string, ruleId: string) =>
    request<void>(`/teams/${teamId}/join-rules/${ruleId}`, { method: 'DELETE' }),
  createProject: (teamId: string, project: any) =>
    request<any>(`/teams/${teamId}/projects`, {
      method: 'POST',
      body: JSON.stringify(project),
    }),
  createBundle: (teamId: string, bundle: any) =>
    request<any>(`/teams/${teamId}/bundles`, {
      method: 'POST',
      body: JSON.stringify(bundle),
    }),
  createActivity: (teamId: string, activity: any) =>
    request<any>(`/teams/${teamId}/activities`, {
      method: 'POST',
      body: JSON.stringify(activity),
    }),
  createAttribute: (teamId: string, attribute: any) =>
    request<any>(`/teams/${teamId}/attributes`, {
      method: 'POST',
      body: JSON.stringify(attribute),
    }),
  updateAttribute: (teamId: string, attributeId: string, attribute: any) =>
    request<any>(`/teams/${teamId}/attributes/${attributeId}`, {
      method: 'PATCH',
      body: JSON.stringify(attribute),
    }),
  deleteAttribute: (teamId: string, attributeId: string) =>
    request<void>(`/teams/${teamId}/attributes/${attributeId}`, { method: 'DELETE' }),
  createEmailRule: (teamId: string, rule: any) =>
    request<any>(`/teams/${teamId}/email-rules`, {
      method: 'POST',
      body: JSON.stringify(rule),
    }),
  deleteEmailRule: (teamId: string, ruleId: string) =>
    request<void>(`/teams/${teamId}/email-rules/${ruleId}`, { method: 'DELETE' }),
  createFeed: (teamId: string, feed: any) =>
    request<any>(`/teams/${teamId}/feeds`, {
      method: 'POST',
      body: JSON.stringify(feed),
    }),
  revokeFeed: (teamId: string, feedId: string) =>
    request<void>(`/teams/${teamId}/feeds/${feedId}`, { method: 'DELETE' }),
  createConnection: (teamId: string, connection: any) =>
    request<any>(`/teams/${teamId}/connections`, {
      method: 'POST',
      body: JSON.stringify(connection),
    }),
  deleteConnection: (teamId: string, connectionId: string) =>
    request<void>(`/teams/${teamId}/connections/${connectionId}`, { method: 'DELETE' }),
};

// User API
export const userApi = {
  create: (user: any) =>
    request<any>('/auth/register', {
      method: 'POST',
      body: JSON.stringify({
        email: user.email,
        password: 'anonymous-password', // Dummy password for anonymous users
        displayName: user.displayName,
      }),
    }),
  getProfile: () => request<any>('/user/me'),
  getEmails: () => request<any>('/user/me/emails'),
  getPendingEmails: () => request<any>('/user/me/emails/pending'),
  linkEmail: (payload: { email: string }) =>
    request<any>('/user/me/emails/link', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  verifyEmail: (payload: { token: string }) =>
    request<any>('/user/me/emails/verify', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  previewEmailVerification: (payload: { token: string }) =>
    request<any>('/user/me/emails/verify/preview', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  updateProfile: (updates: any) =>
    request<any>('/user/me', {
      method: 'PATCH',
      body: JSON.stringify(updates),
    }),
  getSettings: () => request<any>('/user/me/settings'),
  updateSettings: (settings: any) =>
    request<any>('/user/me/settings', {
      method: 'PUT',
      body: JSON.stringify(settings),
    }),
  completeOnboarding: (payload: any) =>
    request<any>('/user/me/onboarding', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  getConstraints: () => request<any>('/user/me/constraints'),
  addConstraint: (constraint: any) =>
    request<any>('/user/me/constraints', {
      method: 'POST',
      body: JSON.stringify(constraint),
    }),
  deleteConstraint: (id: number) =>
    request<void>(`/user/me/constraints/${id}`, { method: 'DELETE' }),
  getAttributes: () => request<any>('/attributes'),
  addAttribute: (attr: any) =>
    request<any>('/attributes', {
      method: 'POST',
      body: JSON.stringify(attr),
    }),
  updateAttribute: (id: string, attr: any) =>
    request<any>(`/attributes/${id}`, {
      method: 'PUT',
      body: JSON.stringify(attr),
    }),
  deleteAttribute: (id: string) =>
    request<void>(`/attributes/${id}`, { method: 'DELETE' }),
};

// Auth API
export const authApi = {
  saidExchange: (data: { token: string }) =>
    request<any>('/auth/said/exchange', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  login: (credentials: { email: string; password: string }) =>
    request<any>('/auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    }),
  sahastLogin: (credentials: { identifier: string; password: string }) =>
    request<any>('/auth/sahast/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    }),
  mailExchange: (data: { token: string }) =>
    request<any>('/auth/mail/exchange', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  oauthProviders: () => request<any>('/auth/oauth/providers'),
  zohoAuthUrl: (returnTo = '/teams') =>
    request<{ url: string }>(`/auth/zoho/url?return_to=${encodeURIComponent(returnTo)}`),
  zohoLinkAuthUrl: (returnTo = '/settings') =>
    request<{ url: string }>(`/auth/zoho/link-url?return_to=${encodeURIComponent(returnTo)}`),
  githubAuthUrl: (returnTo = '/teams') =>
    request<{ url: string }>(`/auth/github/url?return_to=${encodeURIComponent(returnTo)}`),
  githubLinkAuthUrl: (returnTo = '/settings') =>
    request<{ url: string }>(`/auth/github/link-url?return_to=${encodeURIComponent(returnTo)}`),
  register: (data: { email: string; password: string; displayName: string }) =>
    request<any>('/auth/register', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  logout: () =>
    request<void>('/auth/logout', {
      method: 'POST',
    }),
  session: () => request<any>('/auth/session'),
};

// Combined API export
export const api = {
  tasks: tasksApi,
  schedule: scheduleApi,
  nlp: nlpApi,
  calendar: calendarApi,
  ocr: ocrApi,
  training: trainingApi,
  state: stateApi,
  workspace: workspaceApi,
  teams: teamsApi,
  user: userApi,
  auth: authApi,
};
